import { useState, useEffect, JSX } from 'react';

// Define the translation function type that supports interpolation
type TranslationFunction = (
  page: string,
  context: string,
  fallback?: string,
  interpolation?: Record<string, string | number | boolean>
) => string;
import {
  GAP_SCALE,
  PADDING_X,
  PADDING_Y,
  COLOR_SCALE,
  RADIUS_SCALE,
  TYPE_SCALE,
  INTERACTIVE_STATES,
  SIZE_SCALE,
  DISPLAY,
  POSITION_SCALE,
} from '@/components/design-system/tokens';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import { useTranslation } from '@/hooks/useTranslation';

interface Section {
  id: string;
  label: string;
}

interface SectionIndicatorProps {
  sections?: Section[];
}

/**
 * Section Indicator Component
 * Shows dots indicating which section of the page is currently active
 */
function SectionIndicator({ sections = [] }: SectionIndicatorProps): JSX.Element | null {
  const { t } = useTranslation() as {
    t: TranslationFunction;
    currentLanguage: string;
    changeLanguage: (lang: string) => void;
    loading: boolean;
    forceUpdate: number;
    translations: Record<string, unknown>;
    fallbackTranslations: Record<string, unknown>;
    debugTranslations: () => void;
  };
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px', // Trigger when section is in center of viewport
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]): void => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all sections
    for (const section of sections) {
      const element = document.querySelector(`#${section.id}`);
      if (element) {
        observer.observe(element);
      }
    }

    return () => {
      try {
        observer.disconnect();
      } catch {
        // Ignore cleanup errors during HMR
      }
    };
  }, [sections]);

  const scrollToSection = (sectionId: string): void => {
    const element = document.querySelector(`#${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (typeof window !== 'undefined') {
        const { pathname, search } = window.location;
        const newUrl = `${pathname}${search}#${sectionId}`;
        if (typeof window.history.replaceState === 'function') {
          window.history.replaceState(null, '', newUrl);
        } else {
          window.location.hash = sectionId;
        }
      }
    }
  };

  if (sections.length === 0) return null;

  return (
    <div
      className={`fixed right-6 top-1/2 transform -translate-y-1/2 z-40 hidden lg:flex flex-col ${GAP_SCALE['3']}`}
    >
      {sections.map(section => (
        <button
          key={section.id}
          onClick={() => scrollToSection(section.id)}
          className={`${DISPLAY.group} ${POSITION_SCALE.relative}`}
          aria-label={t('aria', 'navigate_to_section', 'Navigate to {{section}}', {
            section: section.label,
          })}
        >
          <div
            className={`${SIZE_SCALE['w2.5']} ${SIZE_SCALE['2.5']} ${RADIUS_SCALE.full} ${INTERACTIVE_STATES.transitions.slow} ${
              activeSection === section.id
                ? `${COLOR_SCALE.foreground} scale-125`
                : `${COLOR_SCALE['admin-muted-text']} hover:${COLOR_SCALE['admin-subtext']}`
            }
            }`}
          />
          {/* Tooltip */}
          <span
            className={`absolute right-6 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 ${INTERACTIVE_STATES.transitions.colors} ${COLOR_SCALE.foreground} text-white ${TYPE_SCALE.xs} ${PADDING_X['3']} ${PADDING_Y['1.5']} ${RADIUS_SCALE.md} whitespace-nowrap pointer-events-none`}
          >
            {section.label}
          </span>
        </button>
      ))}
    </div>
  );
}

const SectionIndicatorWithMonitor = withMemoryLeakMonitor(SectionIndicator, {
  componentName: 'SectionIndicator',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: false, // Section changes can cause renders
});

export default SectionIndicatorWithMonitor;
